import { useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { CoinMascot, Confetti } from "@/components/brand/CoinMascot";
import { Logo } from "@/components/brand/Logo";
import { PRIORITIES } from "@/components/onboarding/parts";
import { StepAsset, StepGoals, StepHousehold } from "@/components/onboarding/Steps";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useHousehold } from "@/context/HouseholdContext";
import { api, formatApiError } from "@/lib/api";

const STEPS = [
  { title: "Votre foyer", text: "Quelques mots sur votre chez-vous.", Comp: StepHousehold },
  { title: "Vos priorités", text: "Nous organisons votre tableau de bord autour de ce qui compte pour vous.", Comp: StepGoals },
  { title: "Un premier équipement", text: "Commencez votre inventaire avec un seul bien. Vous compléterez plus tard.", Comp: StepAsset },
];

const orderWidgets = (current, priorities) => {
  const wanted = PRIORITIES.filter((p) => priorities.includes(p.key)).flatMap((p) => p.widgets);
  const first = ["alerts", ...wanted];
  const sorted = [...current].sort((a, b) => {
    const ia = first.indexOf(a.id), ib = first.indexOf(b.id);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
  });
  return sorted.map((w) => ({ ...w, visible: w.id === "market" ? priorities.includes("sell") || w.visible : w.visible }));
};

async function saveStep(step, v, ctx, saved) {
  const hid = ctx.active.id;
  if (step === 0 && v.name.trim().length >= 2) {
    await api.patch(`/households/${hid}`, { name: v.name.trim(), country: v.country, currency: v.currency, housing_type: v.housing_type || null });
  }
  if (step === 1) {
    if (v.priorities.length) await api.patch("/me/preferences", { widgets: orderWidgets(ctx.user.preferences.widgets, v.priorities) });
    if (!saved.goal && v.goal_name.trim().length >= 2 && Number(v.goal_target) > 0) {
      await api.post(`/households/${hid}/goals`, { name: v.goal_name.trim(), target: Number(v.goal_target) });
      saved.goal = true;
    }
  }
  if (step === 2 && !saved.asset && v.asset_name.trim().length >= 2) {
    await api.post(`/households/${hid}/assets`, {
      name: v.asset_name.trim(), category: v.asset_category, room: v.asset_room || "Salon", brand: v.asset_brand || null,
      purchase_date: v.asset_date || null, purchase_price: v.asset_price ? Number(v.asset_price) : null,
    });
    saved.asset = true;
  }
}

export default function Onboarding() {
  const { user, setUser } = useAuth();
  const { active } = useHousehold();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const saved = useRef({ goal: false, asset: false });
  const [v, setV] = useState({
    name: active?.name || "", country: active?.country || "FR", currency: active?.currency || "EUR", housing_type: "",
    priorities: [], goal_name: "", goal_target: "",
    asset_name: "", asset_category: "other", asset_room: "", asset_brand: "", asset_date: "", asset_price: "",
  });
  const set = (patch) => setV((s) => ({ ...s, ...patch }));
  const done = step === STEPS.length;

  const next = async () => {
    setBusy(true);
    try {
      await saveStep(step, v, { active, user }, saved.current);
      setStep(step + 1);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const finish = async () => {
    try {
      const { data } = await api.post("/me/onboarding/complete");
      setUser(data);
      navigate("/", { replace: true });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!active) return <Navigate to="/" replace />;

  const S = STEPS[step];
  return (
    <div className="flex min-h-screen flex-col" data-testid="onboarding-page">
      <header className="mx-auto flex w-full max-w-2xl items-center justify-between px-6 pt-6">
        <Logo size={32} />
        {!done && <button onClick={finish} className="text-sm text-muted-foreground hover:text-foreground" data-testid="onboarding-skip-button">Passer</button>}
      </header>
      <div className="mx-auto mt-6 flex w-full max-w-2xl gap-2 px-6" data-testid="onboarding-progress">
        {STEPS.map((_, i) => <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors duration-500 ${i <= step ? "bg-sage" : "bg-secondary"}`} />)}
      </div>
      <main className="mx-auto w-full max-w-2xl flex-1 px-6 pb-32 pt-10">
        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -24 }} transition={{ duration: 0.3 }}>
            {done ? (
              <div className="flex flex-col items-center pt-10 text-center" data-testid="onboarding-done">
                <Confetti />
                <CoinMascot mood="celebrate" size={120} />
                <h1 className="mt-8 text-3xl font-extrabold sm:text-4xl">Bienvenue chez vous, {user.name.split(" ")[0]} !</h1>
                <p className="mt-3 max-w-md text-muted-foreground">Votre espace CASHMY est prêt. Vous pourrez tout compléter à votre rythme.</p>
                <Button className="mt-8 h-12 rounded-full px-8" onClick={finish} data-testid="onboarding-finish-button">Découvrir mon tableau de bord<ArrowRight className="ml-2 h-4 w-4" /></Button>
              </div>
            ) : (
              <>
                <div className="eyebrow">Étape {step + 1} sur {STEPS.length}</div>
                <h1 className="mt-2 text-3xl font-extrabold sm:text-4xl" data-testid="onboarding-step-title">{S.title}</h1>
                <p className="mt-2 text-muted-foreground">{S.text}</p>
                <div className="mt-8"><S.Comp v={v} set={set} /></div>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </main>
      {!done && (
        <footer className="glass fixed inset-x-0 bottom-0 border-t safe-bottom">
          <div className="mx-auto flex max-w-2xl items-center gap-3 px-6 py-4">
            {step > 0 && <Button variant="ghost" className="rounded-full" onClick={() => setStep(step - 1)} data-testid="onboarding-back-button"><ArrowLeft className="mr-1 h-4 w-4" />Retour</Button>}
            <Button className="ml-auto h-11 rounded-full px-6" onClick={next} disabled={busy} data-testid="onboarding-next-button">
              {step === STEPS.length - 1 ? "Terminer" : "Continuer"}<ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </footer>
      )}
    </div>
  );
}
